import { supabase } from '@/lib/supabase';
import { formatDate, formatMoney, type FinanceInvoice } from '@/lib/finance';
import type { CompanyProfile } from '@/lib/companyProfile';

// ─────────────────────────────────────────────────────────────
// ZIFEK FINANCE — Paiements partiels & relances de factures
// Chaque encaissement est une ligne de `finance_invoice_payments`.
// Le montant payé de la facture est recalculé à partir de ces lignes :
// l'encaissé suit donc réellement ce qui a été reçu, pas une estimation.
// ─────────────────────────────────────────────────────────────

export type PaymentChannel = 'email' | 'whatsapp';

export interface InvoicePayment {
  id: number;
  invoice_id: number;
  merchant_id: number;
  amount: number;
  currency: string | null;
  paid_at: string;
  method: string | null;
  note: string | null;
  created_at: string;
}

export const PAYMENT_METHODS_SHORT = ['Espèces', 'Virement', 'Chèque', 'Carte', 'CMI', 'Autre'];

/** Somme réellement encaissée sur une facture (lignes de paiement). */
export async function fetchInvoicePayments(invoiceId: number): Promise<InvoicePayment[]> {
  const { data, error } = await supabase
    .from('finance_invoice_payments')
    .select('*')
    .eq('invoice_id', invoiceId)
    .order('paid_at', { ascending: false });
  if (error) throw error;
  return (data || []) as InvoicePayment[];
}

export interface RecordPaymentInput {
  invoice: FinanceInvoice;
  merchantId: number;
  amount: number;
  paidAt: string;
  method?: string | null;
  note?: string | null;
}

/**
 * Enregistre un encaissement (total ou partiel) puis met à jour le montant payé
 * et le statut de la facture. Le montant ne peut jamais dépasser le reste dû.
 */
export async function recordInvoicePayment(input: RecordPaymentInput): Promise<{ amountPaid: number; status: FinanceInvoice['status'] }> {
  const { invoice, merchantId } = input;
  const total = Number(invoice.total) || 0;
  const alreadyPaid = Number(invoice.amount_paid) || 0;
  const remaining = Math.max(0, total - alreadyPaid);
  const amount = Math.min(Math.max(0, Number(input.amount) || 0), remaining);
  if (amount <= 0) throw new Error('Le montant doit être supérieur à 0 et inférieur au reste à payer.');

  const { error: insErr } = await supabase.from('finance_invoice_payments').insert({
    invoice_id: invoice.id,
    merchant_id: merchantId,
    amount,
    currency: invoice.currency || 'MAD',
    paid_at: input.paidAt || new Date().toISOString().slice(0, 10),
    method: input.method ?? null,
    note: input.note ?? null,
    created_by: merchantId,
  });
  if (insErr) throw insErr;

  const amountPaid = Math.round((alreadyPaid + amount) * 100) / 100;
  const status: FinanceInvoice['status'] = amountPaid >= total ? 'paid' : 'partially_paid';

  const { error: upErr } = await supabase
    .from('finance_invoices')
    .update({ amount_paid: amountPaid, status, updated_at: new Date().toISOString() })
    .eq('id', invoice.id);
  if (upErr) throw upErr;

  return { amountPaid, status };
}

// ── Relances ─────────────────────────────────────────────────

export interface InvoiceReminder {
  id: number;
  invoice_id: number;
  merchant_id: number;
  channel: string;
  recipient: string | null;
  message: string | null;
  sent_at: string;
}

export async function fetchReminders(invoiceId: number): Promise<InvoiceReminder[]> {
  const { data, error } = await supabase
    .from('finance_invoice_reminders')
    .select('*')
    .eq('invoice_id', invoiceId)
    .order('sent_at', { ascending: false });
  if (error) throw error;
  return (data || []) as InvoiceReminder[];
}

export async function logReminder(input: {
  invoiceId: number;
  merchantId: number | null;
  channel: PaymentChannel;
  recipient?: string | null;
  message: string;
}): Promise<void> {
  if (!input.merchantId) return;
  try {
    await supabase.from('finance_invoice_reminders').insert({
      invoice_id: input.invoiceId,
      merchant_id: input.merchantId,
      channel: input.channel,
      recipient: input.recipient ?? null,
      message: input.message,
    });
  } catch {
    /* l'historique ne doit jamais bloquer la relance */
  }
}

/** Nombre de jours de retard (0 si non échue). */
export function daysLate(invoice: FinanceInvoice): number {
  if (!invoice.due_date) return 0;
  const due = new Date(invoice.due_date);
  if (Number.isNaN(due.getTime())) return 0;
  const diff = Date.now() - due.getTime();
  return diff > 0 ? Math.floor(diff / 86400000) : 0;
}

/** Une facture est « à relancer » dès qu'il reste un montant dû. */
export function needsReminder(invoice: FinanceInvoice): boolean {
  if ((invoice.doc_type || 'invoice') !== 'invoice') return false;
  if (invoice.status === 'cancelled' || invoice.status === 'draft') return false;
  return Math.max(0, Number(invoice.total) - Number(invoice.amount_paid)) > 0.009;
}

export function isOverdue(invoice: FinanceInvoice): boolean {
  return needsReminder(invoice) && daysLate(invoice) > 0;
}

/** Message de rappel pré-rempli (e-mail / WhatsApp). */
export function buildReminderMessage(invoice: FinanceInvoice, profile: CompanyProfile): string {
  const remaining = Math.max(0, Number(invoice.total) - Number(invoice.amount_paid));
  const cur = invoice.currency || 'MAD';
  const late = daysLate(invoice);
  const lines = [
    `Bonjour${invoice.customer_name ? ` ${invoice.customer_name}` : ''},`,
    '',
    `Nous vous rappelons que la facture n° ${invoice.number} d'un montant de ${formatMoney(
      Number(invoice.total),
      cur,
    )} reste à régler pour ${formatMoney(remaining, cur)}.`,
  ];
  if (invoice.due_date) {
    lines.push(
      late > 0
        ? `L'échéance du ${formatDate(invoice.due_date)} est dépassée de ${late} jour${late > 1 ? 's' : ''}.`
        : `L'échéance est fixée au ${formatDate(invoice.due_date)}.`,
    );
  }
  if (invoice.payment_method) lines.push(`Mode de paiement convenu : ${invoice.payment_method}.`);
  lines.push(
    '',
    'Si le règlement a déjà été effectué, merci de ne pas tenir compte de ce message.',
    '',
    profile.company_name || '',
  );
  return lines.filter((l) => l !== undefined).join('\n');
}

export function reminderMailto(invoice: FinanceInvoice, profile: CompanyProfile): string {
  const subject = `Rappel de paiement — Facture ${invoice.number}${
    profile.company_name ? ` — ${profile.company_name}` : ''
  }`;
  const body = buildReminderMessage(invoice, profile);
  return `mailto:${invoice.customer_email || ''}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
}

export function reminderWhatsAppUrl(invoice: FinanceInvoice, profile: CompanyProfile): string {
  const phone = String(invoice.customer_phone || '').replace(/[^\d]/g, '');
  const message = buildReminderMessage(invoice, profile);
  return phone
    ? `https://wa.me/${phone}?text=${encodeURIComponent(message)}`
    : `https://wa.me/?text=${encodeURIComponent(message)}`;
}